import React from 'react';
import { withFormsy } from 'formsy-react';
import { IconButton } from '@mui/material';

import BaseTextField, { type FieldProps } from './components/BaseTextField';
import Icon from '../Icon';

type NumberTextFieldProps = {
	/** The interval between legal numbers */
	step?: number;
	/** If provided, the field measure unit (as end adornment) */
	measureUnit?: string;
} & FieldProps<string | number>;

/** An input component used to display and edit text-based numeric values */
function NumberTextField({ step = 1, ...props }: NumberTextFieldProps) {
	const { minValue, maxValue } = props.validations || {};

	function parseValue(newValue?: string | number) {
		return typeof newValue === 'string' ? (newValue ? Number.parseFloat(newValue) : undefined) : newValue;
	}

	function incrementValue() {
		const newValue = (parseValue(props.value ?? 0) || 0) + step;
		props.setValue(newValue);
		handleChange(newValue);
	}

	function decrementValue() {
		const newValue = (parseValue(props.value ?? 0) || 0) - step;
		props.setValue(newValue);
		handleChange(newValue);
	}

	function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleChange(event.currentTarget.value);
		}
	}

	function handleChange(newValue?: string | number) {
		const parsedValue = parseValue(newValue);
		const formattedValue = parsedValue || parsedValue === 0 ? parsedValue : null;
		if (props.beforeChange) props.beforeChange(parsedValue);
		props.sendChange(props.name, formattedValue);
		if (props.afterChange) props.afterChange(parsedValue);
	}

	return (
		<BaseTextField
			className={props.className}
			name={props.name}
			label={props.label}
			type="number"
			inputMode="decimal"
			value={props.value || ''}
			helperText={props.helperText}
			isEdit={props.isEdit}
			startAdornment={
				!props.isEdit ? (
					props.startAdornment
				) : (
					<>
						<IconButton size="small" onClick={decrementValue} disabled={(props.value || 0) <= minValue}>
							<Icon icon="minus" size={16} />
						</IconButton>
						<IconButton size="small" onClick={incrementValue} disabled={(props.value || 0) >= maxValue}>
							<Icon icon="plus" size={16} />
						</IconButton>
					</>
				)
			}
			endAdornment={
				props.measureUnit ? <div className="pt-16 leading-none">{props.measureUnit}</div> : props.endAdornment
			}
			required={props.required}
			errorMessage={props.errorMessage}
			isFormSubmitted={props.isFormSubmitted}
			isPristine={props.isPristine}
			isValid={props.isValid}
			logProps={props.logProps}
			onKeyDown={handleKeyDown}
			onChange={event => props.setValue(parseValue(event.target.value) || null)}
			onBlur={event => handleChange(event.target.value)}
		/>
	);
}

export default React.memo(withFormsy(NumberTextField));
